import { useSyncExternalStore } from 'react'

/*
 * The theme the visitor is actually seeing, as opposed to the preference they
 * chose -- `system` resolves against the OS.
 *
 * This reads the `data-theme` attribute rather than sharing state with
 * ThemeToggle, because the attribute is what tokens.css keys off: if this
 * disagreed with the attribute, the logo would disagree with the page around it.
 * Both inputs are observed, since either can change without the other.
 */
function subscribe(onChange: () => void): () => void {
    const observer = new MutationObserver(onChange)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })

    const media = window.matchMedia('(prefers-color-scheme: light)')
    media.addEventListener('change', onChange)

    return () => {
        observer.disconnect()
        media.removeEventListener('change', onChange)
    }
}

/* A string, so it is its own stable snapshot -- no caching needed. */
function snapshot(): 'dark' | 'light' {
    const attr = document.documentElement.getAttribute('data-theme')
    if (attr === 'dark' || attr === 'light') return attr
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

export function useResolvedTheme(): 'dark' | 'light' {
    return useSyncExternalStore(subscribe, snapshot, () => 'dark')
}
